import { useState } from "react"
import type { Chapter } from "@/lib/api/textbooks"

export type SavedProgress = {
  textbookId: string | null
  chapterNumber: number | null
}

export type ProgressSelection = { textbookId: string; chapterNumber: number }

/**
 * Textbook + chapter selection on top of the saved progress. Picks stay
 * pending until saved; `pendingSave` is the payload to save, or null when
 * there's nothing (complete) to save.
 */
export function useProgressSelection(saved: SavedProgress | null | undefined) {
  const [selectedTextbookId, setSelectedTextbookId] = useState<string | null>(
    null
  )
  const textbookId = selectedTextbookId ?? saved?.textbookId ?? null

  // Pending chapter pick, keyed by textbook so switching textbooks doesn't
  // carry a selection across.
  const [pending, setPending] = useState<ProgressSelection | null>(null)

  const savedChapter =
    textbookId !== null && textbookId === saved?.textbookId
      ? (saved.chapterNumber ?? null)
      : null
  const chapterNumber =
    pending?.textbookId === textbookId ? pending.chapterNumber : savedChapter

  const isChanged =
    textbookId !== (saved?.textbookId ?? null) ||
    chapterNumber !== (saved?.chapterNumber ?? null)

  const pendingSave =
    isChanged && textbookId !== null && chapterNumber !== null
      ? { textbookId, chapterNumber }
      : null

  // Switching textbooks clears any pending pick; the saved textbook reselects
  // its saved chapter.
  function selectTextbook(id: string) {
    if (id === textbookId) return
    setSelectedTextbookId(id)
    setPending(null)
  }

  function selectChapter(number: number) {
    if (textbookId) setPending({ textbookId, chapterNumber: number })
  }

  return {
    textbookId,
    chapterNumber,
    isChanged,
    pendingSave,
    selectTextbook,
    selectChapter,
  }
}

/** Words and kanji introduced up to and including chapter `upTo`. */
export function cumulativeCounts(chapters: Chapter[], upTo: number | null) {
  return chapters
    .filter((c) => upTo !== null && c.number <= upTo)
    .reduce(
      (acc, c) => ({
        wordCount: acc.wordCount + c.wordCount,
        kanjiCount: acc.kanjiCount + c.kanjiCount,
      }),
      { wordCount: 0, kanjiCount: 0 }
    )
}
